import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Single reusable card surface — all values come from the --card-* tokens
// (index.css) so every content card stays 1:1 consistent across the app.
//  • default → solid white, hairline border, soft subtle shadow (18px radius)
//  • tinted  → faint top accent wash for emphasis cards
//  • hero    → frosted glass, only for large summary/stat blocks
const cardVariants = cva(
  "text-card-foreground rounded-[var(--card-radius)] transition-all duration-200",
  {
    variants: {
      variant: {
        default:
          "bg-white border border-[color:var(--card-border-color)] shadow-[var(--card-shadow)]",
        tinted:
          "border border-[color:var(--card-border-color)] shadow-[var(--card-shadow)] bg-[linear-gradient(180deg,var(--card-tint-accent),rgba(255,255,255,0.6))]",
        hero:
          "bg-white/60 backdrop-blur-[14px] backdrop-saturate-150 border border-white/75 shadow-[0_10px_30px_rgba(20,60,100,0.10),inset_0_1px_0_rgba(255,255,255,0.7)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
  ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-2xl font-heading font-bold leading-none tracking-tight", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
